import { useCallback, useRef } from 'react';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/**
 * A hook that provides a debounced callback mechanism using RxJS
 * @param {Function} callback - The function to be debounced
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} A debounced version of the callback
 */
export const useDebouncedCallback = (callback, wait = 1000) => {
  const subjectRef = useRef(null);
  const callbackRef = useRef(callback);

  // Update the callback if it has changed
  callbackRef.current = callback;

  // Initialize the subject if it doesn't exist
  if (!subjectRef.current) {
    subjectRef.current = new Subject();
    subjectRef.current.pipe(debounceTime(wait)).subscribe((value) => callbackRef.current(value));
  }

  // Return a function that will push values to the subject
  return useCallback((value) => {
    subjectRef.current.next(value);
  }, []);
};
